import { useLoadingStore } from '@/stores/use-loading-store'
import type { LoadingType } from '@/types/loading-type'
import { apiResponseSchema, type ApiResponse } from '~library/services'
import { isType, parseResponse } from '~library/utils/functions'

export async function isLoading<T extends Promise<unknown>>(
  api: T,
  loadingType?: string,
  enableLoading?: boolean
): Promise<Awaited<T> extends ApiResponse ? Awaited<T>['data'] : Awaited<T>>

export async function isLoading<T extends readonly Promise<unknown>[]>(
  api: [...T],
  loadingType?: string,
  enableLoading?: boolean
): Promise<{
  [K in keyof T]: Awaited<T[K]> extends ApiResponse
    ? Awaited<T[K]>['data']
    : Awaited<T[K]>
}>

export async function isLoading<
  T extends Promise<unknown> | readonly Promise<unknown>[],
  R = T extends Promise<unknown>
    ? Awaited<T> extends ApiResponse
      ? Awaited<T>['data']
      : Awaited<T>
    : Promise<{
        [K in keyof T]: Awaited<T[K]> extends ApiResponse
          ? Awaited<T[K]>['data']
          : Awaited<T[K]>
      }>
>(
  api: T,
  loadingType: string = 'spinner' satisfies LoadingType,
  enableLoading = true
): Promise<R> {
  const { addLoading, closeLoading } = useLoadingStore()

  if (enableLoading) {
    addLoading(loadingType)
  }

  try {
    if (Array.isArray(api)) {
      const orderPromiseAll = await getOrderPromiseAll(api)

      return orderPromiseAll.map((response) => {
        return getResponse(response)
      }) as R
    } else {
      return getResponse(await api) as R
    }
  } finally {
    if (enableLoading) {
      closeLoading(loadingType)
    }
  }
}

async function getOrderPromiseAll(apis: Promise<unknown>[]) {
  return apis.map(async (api) => await api)
}

function getResponse(response: unknown) {
  if (!isType(response, apiResponseSchema)) {
    return response
  }

  return parseResponse(response as ApiResponse)
}
